import { useCallback, useState, useEffect, memo } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useTestFunc } from '@carvana/test-attributes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Popup, { Option } from '@carvana/ds-popup';
import useModal from '@carvana/ds-modal';
import { ErrorFilledCvna } from '@carvana/ds-icons';
import { AlertTriangle, Ellipsis } from 'lucide-react';
import { icon } from '@carvana/ds-styles';
import Banner from '@carvana/ds-banner';
import Button from '@carvana/ds-button';
import {
  StyledBanner,
  TileCard,
  ImageWrapper,
  ContentWrapper,
  PriceExpirationWrapper,
  VehicleInfoWrapper,
  VehicleValue,
  YearMakeModel,
  TileActionsButton,
  TileActionModalContent,
  TileActionModalTitle,
  TileActionModalBody,
  TileActionModalButtons
} from './SummaryTiles.styles';
import { useVehicleUrls, useBadge, useVehicleDisplayData, useButtonText } from './hooks';
import { LifecycleState } from 'api/GraphQL/queries/getVehicleSummaries';
import type { VehicleSummary } from 'api/GraphQL/queries/getVehicleSummaries';
import useAnalyticEvent from 'shared/hooks/useAnalyticEvent';
import useImageQuery from 'shared/hooks/useImageQuery';
import Api from 'api/Api';
import { getSummariesQueryKey } from 'shared/hooks/useGetSummaries';

const tileImageLoader = ({ src, width }: { src: string; width: number }) =>
  src.includes('?') ? `${src}&w=${width}` : `${src}?w=${width}`;

const coverStyle = { objectFit: 'cover' as const };

type TileImageProps = {
  year: number;
  make: string;
  model: string;
  trim: string;
  color: string;
  alt: string;
  largeTile: boolean;
};

const TileImage = memo(({ year, make, model, trim, color, alt, largeTile }: TileImageProps) => {
  const { data: imageUrl } = useImageQuery({ year, make, model, trim, color, isMobile: true });

  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const showImage = !!imageUrl && !imageError;

  useEffect(() => {
    setImageError(false);
    setImageLoaded(false);
  }, [imageUrl]);

  useEffect(() => {
    if (!imageUrl || imageLoaded || imageError) return undefined;
    const timeout = setTimeout(() => setImageError(true), 5000);
    return () => clearTimeout(timeout);
  }, [imageUrl, imageLoaded, imageError]);

  return (
    <ImageWrapper $hasImage={showImage} $size={largeTile ? 'lg' : 'md'}>
      {showImage && (
        <Image
          loader={tileImageLoader}
          src={imageUrl}
          alt={alt}
          width={largeTile ? 131 : 90}
          height={largeTile ? 105 : 72}
          style={coverStyle}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}
    </ImageWrapper>
  );
});

TileImage.displayName = 'TileImage';

type SummaryTileProps = {
  summary: VehicleSummary;
  index?: number;
  isMobile?: boolean;
  tileCount?: number;
  showTileActions?: boolean;
  onVehicleAction?: (acquisitionName: string, action: 'mark-as-sold' | 'remove-vehicle') => void;
};

const SummaryTile = ({
  summary,
  index,
  isMobile,
  showTileActions = false,
  tileCount,
  onVehicleAction
}: SummaryTileProps) => {
  const router = useRouter();
  const { recordActionClicked } = useAnalyticEvent();
  const getTestAttributes = useTestFunc();
  const queryClient = useQueryClient();

  const {
    acquisitionName,
    appraisalId,
    color,
    daysToExpiration,
    isOfferAccepted,
    lifecycleState,
    make,
    model,
    trim,
    vehicleValue,
    year
  } = summary;

  // Extract URL construction logic
  const { url } = useVehicleUrls({
    acquisitionName,
    appraisalId,
    isOfferAccepted,
    lifecycleState
  });

  // Extract badge logic
  const badge = useBadge({ lifecycleState, daysToExpiration });

  // Extract display data calculations
  const { yearMakeModel, formattedValue } = useVehicleDisplayData({
    year,
    make,
    model,
    vehicleValue
  });

  // Extract button text logic
  const buttonText = useButtonText({ lifecycleState, isOfferAccepted });

  const handleClickTile = useCallback(async () => {
    await recordActionClicked(
      'Landing Page - Tile click',
      'AppraisalCard',
      { status: lifecycleState, summaryCount: tileCount },
      true
    );
    router.push(url);
  }, [recordActionClicked, lifecycleState, router, url, tileCount]);

  const showValue =
    lifecycleState === LifecycleState.Active ||
    lifecycleState === LifecycleState.Scheduled ||
    lifecycleState === LifecycleState.AppliedToPurchase;

  const largeTile = index === 0 && !isMobile;
  const firstTile = index === 0;

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuKey, setMenuKey] = useState(0);

  const {
    Modal: VehicleActionModal,
    openModal: openVehicleActionModal,
    closeModal: closeVehicleActionModal
  } = useModal();
  const [pendingAction, setPendingAction] = useState<'mark-as-sold' | 'remove-vehicle' | null>(
    null
  );

  const invalidateSummaries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getSummariesQueryKey(true) });
  }, [queryClient]);

  const {
    mutate: mutateMarkAsSold,
    isPending: isMarkingAsSold,
    isError: isMarkAsSoldError,
    reset: resetMarkAsSold
  } = useMutation({
    mutationFn: () => Api.appraisalBff.customerReportVehicleOutcome(acquisitionName, 'MarkedSold'),
    onSuccess: () => {
      closeVehicleActionModal();
      setPendingAction(null);
      resetMarkAsSold();
      setMenuKey(k => k + 1);
      invalidateSummaries();
    }
  });

  const {
    mutate: mutateRemoveVehicle,
    isPending: isRemovingVehicle,
    isError: isRemoveVehicleError,
    reset: resetRemoveVehicle
  } = useMutation({
    mutationFn: () =>
      Api.appraisalBff.customerReportVehicleOutcome(acquisitionName, 'MarkedRemoved'),
    onSuccess: () => {
      closeVehicleActionModal();
      setPendingAction(null);
      resetRemoveVehicle();
      setMenuKey(k => k + 1);
      invalidateSummaries();
    }
  });

  const isPending = isMarkingAsSold || isRemovingVehicle;
  const isMutationError = isMarkAsSoldError || isRemoveVehicleError;

  const handleModalClose = useCallback(() => {
    closeVehicleActionModal();
    setPendingAction(null);
    resetMarkAsSold();
    resetRemoveVehicle();
    setMenuKey(k => k + 1);
  }, [closeVehicleActionModal, resetMarkAsSold, resetRemoveVehicle]);

  // ds-popup and ds-modal each have separate globalPortalRegistry singletons, so ds-modal's
  // shouldIgnoreOutsideClick never recognises the Popup portal as "inside a portal."
  // Intercept mousedown in capture phase (before ds-modal's bubble-phase handler) and stop
  // propagation when the click lands inside any element with data-portal-id — the attribute
  // ds-popup stamps on its portal container.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const blockPortalMousedown = (e: MouseEvent) => {
      let el = e.target as Element | null;
      while (el && el !== document.body) {
        if (el.hasAttribute('data-portal-id')) {
          e.stopImmediatePropagation();
          return;
        }
        el = el.parentElement;
      }
    };
    document.addEventListener('mousedown', blockPortalMousedown, true);
    return () => document.removeEventListener('mousedown', blockPortalMousedown, true);
  }, [menuOpen]);

  const handleMarkAsSold = useCallback(() => {
    setMenuOpen(false);
    setMenuKey(k => k + 1);
    recordActionClicked('Landing Page - Mark as Sold', 'VehicleTileActions', { acquisitionName });
    if (onVehicleAction) {
      onVehicleAction(acquisitionName, 'mark-as-sold');
    } else {
      resetMarkAsSold();
      resetRemoveVehicle();
      setPendingAction('mark-as-sold');
      openVehicleActionModal();
    }
  }, [
    acquisitionName,
    onVehicleAction,
    openVehicleActionModal,
    recordActionClicked,
    resetMarkAsSold,
    resetRemoveVehicle
  ]);

  const handleRemoveVehicle = useCallback(() => {
    setMenuOpen(false);
    setMenuKey(k => k + 1);
    recordActionClicked('Landing Page - Remove Vehicle', 'VehicleTileActions', { acquisitionName });
    if (onVehicleAction) {
      onVehicleAction(acquisitionName, 'remove-vehicle');
    } else {
      resetMarkAsSold();
      resetRemoveVehicle();
      setPendingAction('remove-vehicle');
      openVehicleActionModal();
    }
  }, [
    acquisitionName,
    onVehicleAction,
    openVehicleActionModal,
    recordActionClicked,
    resetMarkAsSold,
    resetRemoveVehicle
  ]);

  const vehicleActionContent =
    pendingAction === 'mark-as-sold'
      ? {
          title: 'Are you sure?',
          body: "You'll no longer receive value updates once you mark this vehicle as sold.",
          confirmText: 'Yes, Mark as Sold',
          onConfirm: mutateMarkAsSold
        }
      : {
          title: 'Are you sure?',
          body: "You'll no longer receive value updates once you remove this vehicle.",
          confirmText: 'Yes, Remove Vehicle',
          onConfirm: mutateRemoveVehicle
        };

  return (
    <>
      <TileCard
        {...getTestAttributes('SummaryTile', appraisalId.toString())}
        onClick={handleClickTile}
        $size={isMobile ? 'md' : 'lg'}
      >
        {showTileActions && (
          // Stop propagation so option clicks don't bubble to TileCard's onClick
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div onClick={e => e.stopPropagation()}>
            <Popup
              key={menuKey}
              control={
                <TileActionsButton
                  type="button"
                  aria-label="Vehicle actions"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={e => {
                    e.stopPropagation();
                    setMenuOpen(prev => !prev);
                  }}
                >
                  <Ellipsis size={20} />
                </TileActionsButton>
              }
              open={menuOpen}
              onClickOutside={() => setMenuOpen(false)}
              variant="list"
            >
              <Option value="mark-as-sold" onClick={handleMarkAsSold}>
                Mark as Sold
              </Option>
              <Option value="remove-vehicle" onClick={handleRemoveVehicle}>
                Remove Vehicle
              </Option>
            </Popup>
          </div>
        )}
        <ContentWrapper $size={isMobile ? 'md' : 'lg'}>
          <TileImage
            year={year || 0}
            make={make || ''}
            model={model || ''}
            trim={trim || ''}
            color={color || ''}
            alt={yearMakeModel}
            largeTile={largeTile}
          />
          <VehicleInfoWrapper>
            {badge}
            <YearMakeModel>{yearMakeModel}</YearMakeModel>
            {showValue && (
              <PriceExpirationWrapper>
                <VehicleValue $mobileSize={isMobile}>{formattedValue}</VehicleValue>
              </PriceExpirationWrapper>
            )}
          </VehicleInfoWrapper>
        </ContentWrapper>
        <Button
          size="md"
          width={isMobile ? '100%' : '165px'}
          onClick={e => {
            e.stopPropagation();
            handleClickTile();
          }}
          variant={firstTile ? 'primary' : 'tertiary'}
        >
          {buttonText}
        </Button>
      </TileCard>

      {!onVehicleAction && (
        <VehicleActionModal desktopWidth="480px" onClose={handleModalClose} divLine={false}>
          <TileActionModalContent id="summary-tile-action-modal-content">
            <StyledBanner
              inline
              type="critical"
              open={isMutationError}
              icon={<ErrorFilledCvna size={40} color={icon.critical} fill={icon.criticalHover} />}
            >
              <Banner.Message>
                <strong>Something went wrong, please try again.</strong>
              </Banner.Message>
            </StyledBanner>
            <AlertTriangle size={40} color={icon.primary} />
            <TileActionModalTitle>{vehicleActionContent.title}</TileActionModalTitle>
            <TileActionModalBody>{vehicleActionContent.body}</TileActionModalBody>
          </TileActionModalContent>
          <TileActionModalButtons>
            <Button
              size="md"
              width="100%"
              variant="primary"
              onClick={() => vehicleActionContent.onConfirm()}
              disabled={isPending}
            >
              {vehicleActionContent.confirmText}
            </Button>
            <Button size="md" width="100%" variant="tertiary" onClick={handleModalClose}>
              Cancel
            </Button>
          </TileActionModalButtons>
        </VehicleActionModal>
      )}
    </>
  );
};

export default memo(SummaryTile);
